import React, { useEffect, useState } from "react";
import client from "../lib/api";
import { useApp } from "../context/AppContext";
import { useBreaches } from "../lib/alerts";
import { Card } from "../components/ui";
import { downloadManual } from "../lib/manuals";
import { translate as t, BRAND, APP_FULL, LANGS, TL, appTitle } from "../i18n";
import { KeyRound, Download, AlertTriangle, Megaphone, Phone, PhoneOff, HelpCircle, CalendarClock, ShieldAlert, ShieldCheck, FileBarChart, Loader2, FileSpreadsheet, FlaskConical, Trash2, Mail, Headset, Bell, BellRing, Check, RotateCcw, BarChart3, RefreshCw, Newspaper, Trophy, Wallet, CreditCard, Eye } from "lucide-react";
import { APP_LOGOS, APP_WORDMARKS } from "../appLogos";
import { MaintenanceBanner } from "../components/MaintenanceBanner";
import { Link, useNavigate } from "react-router-dom";

const CARDS = [
  { key: "licenses", manual: "master_enabler", to: "/app/enabler", icon: KeyRound, label: "master_enabler" },
  { key: "persons", manual: "family_tree", to: "/app/family", img: APP_LOGOS.family_tree, wordmark: APP_WORDMARKS.family_tree, label: "family_tree" },
  { key: "benefits", manual: "benefits", to: "/app/benefits", banner: APP_WORDMARKS.benefits, label: "benefits" },
  { key: "currencies", manual: "currency", to: "/app/currency", img: APP_LOGOS.currency, wordmark: APP_WORDMARKS.currency, label: "currency" },
  { key: "holdings", manual: "holdings", to: "/app/holdings", img: APP_LOGOS.holdings, wordmark: APP_WORDMARKS.holdings, label: "holdings" },
];

function MyServiceRenewalsTile() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { client.get("/my-service-renewals").then((r) => setData(r.data)).catch(() => {}); }, []);
  const renew = async () => {
    try {
      const { data } = await client.post("/payments/account-service-checkout", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { navigate("/app/account"); }
  };
  if (!data || (data.items || []).length === 0) return null;
  const dot = (lvl) => lvl === "red" ? "bg-[#DA1E28]" : lvl === "yellow" ? "bg-amber-400" : "bg-emerald-500";
  return (
    <div data-testid="dashboard-my-service-renewals" className="block rounded-xl glass p-6 lift">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <RefreshCw size={20} className="text-[var(--primary)]" />
        <span className="font-head text-base">{TL("Service renewal due")}</span>
        <span className="text-xs opacity-60">One fee for all your apps · $7.50/mo billed annually · tax at checkout</span>
      </div>
      <ul className="space-y-2 text-sm" data-testid="my-service-renewals-list">
        {data.items.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center gap-2" data-testid={`my-service-renewal-${r.id}`}>
            <span className={`h-2 w-2 rounded-full animate-pulse ${dot(r.level)}`} />
            <span className="font-medium">{(r.apps || []).map((a) => appTitle(a) || a).join(", ") || TL("All applications")}</span>
            <span className="opacity-60">{r.level === "red" ? `${r.days_overdue}d overdue` : `in ${r.days_to_expiry}d`}</span>
            <button onClick={renew} data-testid={`my-renew-${r.id}`}
              className={`ms-auto rounded-full px-3 py-1 text-xs text-white lift ${r.level === "red" ? "bg-[#DA1E28]" : "bg-[var(--primary)]"}`}>
              Renew now — USD {(r.annual_total_usd ?? 94.5).toFixed(2)}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function NewsletterAnalyticsTile() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const navigate = useNavigate();
  useEffect(() => { client.get("/site/newsletter/analytics", { params: { days } }).then((r) => setData(r.data)).catch(() => {}); }, [days]);
  if (!data) return null;
  const stat = (label, val, testid, sub) => (
    <div className="rounded-lg bg-black/5 dark:bg-white/5 p-3" data-testid={testid}>
      <div className="font-head text-2xl">{val}</div>
      <div className="text-xs opacity-60">{label}</div>
      {sub && <div className="text-[11px] opacity-50 mt-0.5">{sub}</div>}
    </div>
  );
  return (
    <div data-testid="dashboard-newsletter-tile" className="block rounded-xl glass p-6 lift">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Newspaper size={20} className="text-[var(--primary)]" />
        <span className="font-head text-base">{TL("FIDUX™ growth")}</span>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="newsletter-range"
          className="ms-auto rounded-md border border-black/15 dark:border-white/15 bg-transparent px-2 py-1 text-xs">
          <option value={7}>{TL("Last 7 days")}</option>
          <option value={30}>{TL("Last 30 days")}</option>
          <option value={90}>{TL("Last 90 days")}</option>
          <option value={3650}>{TL("All time")}</option>
        </select>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {stat("Active subscribers", data.active_subscribers, "nl-active")}
        {stat("Issues shared", data.shares, "nl-shares", `${data.shares_all_time} all-time`)}
        {stat("New subscribers", data.new_subscribers, "nl-new", `${data.new_from_shares} from shares`)}
        {stat("Unsubscribes", data.unsubscribes, "nl-unsubs")}
      </div>
      {(data.top_sharers || []).length > 0 && (
        <div className="mt-4" data-testid="nl-top-sharers">
          <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide opacity-60">
            <Trophy size={13} /> Top sharers
          </div>
          <ul className="space-y-1">
            {data.top_sharers.map((s, i) => (
              <li key={s.email} className="flex items-center gap-2 text-sm" data-testid={`nl-top-sharer-${i}`}>
                <span className="w-4 text-center opacity-50">{i + 1}</span>
                <span className="truncate">{s.email}</span>
                <span className="ms-auto rounded-full bg-[var(--primary)]/10 px-2 py-0.5 text-xs text-[var(--primary)]">{s.shares} shared</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <button onClick={() => navigate("/app/audiences")} data-testid="nl-manage"
        className="mt-4 text-sm text-[var(--primary)]">{TL("Manage subscribers →")}</button>
    </div>
  );
}

function ServiceRenewalsTile() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { client.get("/admin/service-renewals").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return null;
  const c = data.counts || { green: 0, yellow: 0, red: 0 };
  const dot = (lvl) => lvl === "red" ? "bg-[#DA1E28]" : lvl === "yellow" ? "bg-amber-400" : "bg-emerald-500";
  return (
    <div data-testid="dashboard-service-renewals" onClick={() => navigate("/app/enabler")}
      className="block cursor-pointer rounded-xl glass p-6 lift">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <RefreshCw size={20} className="text-[var(--primary)]" />
        <span className="font-head text-base">{TL("Service renewals")}</span>
        <div className="ms-auto flex items-center gap-3 text-xs">
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" /> {c.green} · 30d</span>
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-amber-400 animate-pulse" /> {c.yellow} · 15d</span>
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-[#DA1E28] animate-pulse" /> {c.red} · overdue</span>
        </div>
      </div>
      {(data.items || []).length === 0 ? (
        <div className="text-sm opacity-50" data-testid="service-renewals-empty">{TL("No renewals due within 30 days.")}</div>
      ) : (
        <ul className="space-y-1 text-sm" data-testid="service-renewals-list">
          {data.items.slice(0, 6).map((r) => (
            <li key={r.id} className="flex items-center gap-2" data-testid={`service-renewal-${r.id}`}>
              <span className={`h-2 w-2 rounded-full animate-pulse ${dot(r.level)}`} />
              <span className="font-medium">{r.email}</span>
              <span className="opacity-60">{(r.apps || []).join(", ")}</span>
              <span className="ms-auto opacity-60">{r.level === "red" ? `${r.days_overdue}d overdue` : `in ${r.days_to_expiry}d`}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FilingWallTile() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    client.get("/admin/compliance-calendar?window_days=180").then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data || (data.filings || []).length === 0) return null;
  const next3 = data.filings.slice(0, 3);
  const tone = (d) => {
    if (d < 0) return { border: "border-black/20", bg: "bg-black/5", text: "text-black/60 dark:text-white/60" };
    if (d < 14) return { border: "border-[#DA1E28]/40", bg: "bg-[#DA1E28]/10", text: "text-[#DA1E28]" };
    if (d < 45) return { border: "border-[#F1C21B]/40", bg: "bg-[#F1C21B]/10", text: "text-[#8A6D0B] dark:text-[#F1C21B]" };
    return { border: "border-[#0F62FE]/40", bg: "bg-[#0F62FE]/10", text: "text-[#0F62FE]" };
  };
  return (
    <div data-testid="dashboard-filing-wall" className="rounded-xl glass p-6 lift">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <CalendarClock size={20} className="text-[var(--primary)]" />
        <span className="font-head text-base">Compliance filing wall</span>
        <span className="text-xs opacity-60">Next 3 filings across all jurisdictions</span>
        <button onClick={() => navigate("/app/reports-compliance")} data-testid="filing-wall-view-all"
          className="ms-auto rounded-full border border-[var(--primary)] px-3 py-1 text-xs text-[var(--primary)] lift">
          View all →
        </button>
      </div>
      <ul className="space-y-2 text-sm" data-testid="filing-wall-list">
        {next3.map((f) => {
          const t = tone(f.days_until);
          return (
            <li key={f.id} className={`flex flex-wrap items-center gap-2 rounded-md border p-2 ${t.border} ${t.bg} ${t.text}`}
              data-testid={`filing-wall-item-${f.id}`}>
              <span className="font-medium">{f.filing_name}</span>
              <span className="rounded-md bg-white/40 dark:bg-black/30 px-1.5 text-[10px]">{f.jurisdiction}</span>
              <span className="text-xs opacity-80">
                Due {new Date(f.next_due).toLocaleDateString()} ·
                <b className="ms-1">{f.days_until >= 0 ? `in ${f.days_until}d` : `${Math.abs(f.days_until)}d overdue`}</b>
              </span>
              {f.assignee && (
                <span className="ms-auto text-xs opacity-70" data-testid={`filing-wall-assignee-${f.id}`}>
                  → {f.assignee.assignee_name} ({f.assignee.role_hint})
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}


function OverdueAccountsTile() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { client.get("/admin/overdue-accounts").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data || data.count === 0) return null;
  const T = data.totals || {};
  const money = (v) => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
  const STAGE = {
    overdue_0: { label: "Overdue", cls: "bg-amber-400/15 text-amber-700 dark:text-amber-400" },
    newsletter_30: { label: "30d · Newsletter cut", cls: "bg-orange-500/15 text-orange-700 dark:text-orange-400" },
    restricted_60: { label: "60d · Updates & Care cut", cls: "bg-red-500/15 text-red-700 dark:text-red-400" },
    suspended_90: { label: "90d · SUSPENDED", cls: "bg-[#DA1E28]/20 text-[#DA1E28] font-semibold" },
  };
  return (
    <div data-testid="dashboard-overdue-accounts" role="button" tabIndex={0}
      onClick={() => navigate("/app/enabler")}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate("/app/enabler"); } }}
      className="block cursor-pointer rounded-xl glass p-6 lift focus:outline-none focus:ring-2 focus:ring-[var(--primary)]">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <AlertTriangle size={20} className="text-[#DA1E28]" />
        <span className="font-head text-base">{TL("Overdue accounts")}</span>
        <span className="rounded-full bg-[#DA1E28]/15 px-2 py-0.5 text-xs font-semibold text-[#DA1E28]" data-testid="overdue-count">{data.count}</span>
        <div className="ms-auto flex flex-wrap items-center gap-2 text-xs">
          <span data-testid="overdue-stage-30">{TL("30d:")} <b>{T.stage_30 || 0}</b></span>
          <span data-testid="overdue-stage-60">{TL("60d:")} <b>{T.stage_60 || 0}</b></span>
          <span className="text-[#DA1E28]" data-testid="overdue-stage-90">{TL("90d:")} <b>{T.stage_90 || 0}</b></span>
        </div>
      </div>
      <ul className="space-y-1 text-sm" data-testid="overdue-accounts-list">
        {data.items.slice(0, 6).map((r) => {
          const s = STAGE[r.stage] || STAGE.overdue_0;
          return (
            <li key={r.id} className="flex flex-wrap items-center gap-2" data-testid={`overdue-account-${r.id}`}>
              <span className="font-medium">{r.email}</span>
              <span className="opacity-60">{(r.apps || []).join(", ")}</span>
              <span className={`rounded px-1.5 py-0.5 text-[11px] ${s.cls}`}>{s.label}</span>
              <span className="ms-auto tabular-nums opacity-60">{r.days_overdue}d overdue</span>
              <span className="tabular-nums opacity-45" title={TL("Late fee (waived)")}>
                {r.late_fee.waived ? "waived " : ""}${money(r.late_fee.amount)}
              </span>
            </li>
          );
        })}
      </ul>
      <div className="mt-3 flex items-center justify-between text-xs opacity-60">
        <span>{TL("Enforcement is automatic: Newsletter at 30d, updates & Customer Care at 60d, access suspended at 90d.")}</span>
        <span data-testid="overdue-late-fee-total">
          Late fees {data.late_fee_waived ? "(waived)" : ""}: ${money(data.late_fee_total)}
        </span>
      </div>
    </div>
  );
}

function SalesTile() {
  const [ov, setOv] = useState(null);
  const [days, setDays] = useState(30);
  const [exporting, setExporting] = useState(false);
  const navigate = useNavigate();
  useEffect(() => { client.get("/site/sales-summary", { params: { days } }).then((r) => setOv(r.data)).catch(() => {}); }, [days]);
  const RANGES = [[7, "7d"], [30, "30d"], [90, "90d"], [0, "All"]];
  const stop = (e) => e.stopPropagation();
  const exportCsv = async (e) => {
    e.stopPropagation();
    setExporting(true);
    try {
      const res = await client.get("/site/sales-summary.csv", { params: { days }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `fopfo-sales-${days ? `last-${days}d` : "all-time"}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { /* noop */ } finally { setExporting(false); }
  };
  if (!ov) return null;
  const cur = (ov.currency || "usd").toUpperCase();
  const rows = ov.by_product || [];
  return (
    <div data-testid="dashboard-sales-tile" onClick={() => navigate("/app/audiences")}
      className="block cursor-pointer rounded-xl glass p-6 lift">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <BarChart3 size={20} className="text-[var(--primary)]" />
        <span className="font-head text-base">{TL("Sales by app")}</span>
        <div className="ms-auto flex items-center gap-1" onClick={stop}>
          {RANGES.map(([d, label]) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`sales-range-${d}`}
              className={`rounded-full px-2.5 py-1 text-xs lift ${days === d ? "bg-[var(--primary)] text-white" : "border border-black/15 dark:border-white/15"}`}>
              {label}</button>
          ))}
          <button onClick={exportCsv} disabled={exporting} data-testid="sales-export-csv"
            className="ms-1 flex items-center gap-1 rounded-full border border-black/15 dark:border-white/15 px-2.5 py-1 text-xs lift hover:bg-black/5 dark:hover:bg-white/10">
            {exporting ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} CSV</button>
        </div>
      </div>
      <div className="mb-3 text-xs opacity-50">{ov.paid_count} paid · {ov.total_revenue.toFixed(2)} {cur}{ov.pending_count ? ` · ${ov.pending_count} pending` : ""}</div>
      {(ov.by_kind || []).length > 0 && (
        <div className="mb-4 grid gap-2 sm:grid-cols-3" data-testid="sales-by-kind">
          {ov.by_kind.map((k) => (
            <div key={k.kind} className="rounded-lg bg-black/5 dark:bg-white/10 p-3" data-testid={`sales-kind-${k.kind}`}>
              <div className="text-xs opacity-60">{k.label}</div>
              <div className="font-head text-lg">{k.revenue.toFixed(2)} {cur}</div>
              <div className="text-xs opacity-50">{k.paid_count} paid{k.pending_count ? ` · ${k.pending_count} pending` : ""}</div>
            </div>
          ))}
        </div>
      )}
      {rows.length === 0 ? (
        <div className="text-sm opacity-50" data-testid="sales-empty">{TL("No payments in this period. Sales from the Solutions page will appear here by application.")}</div>
      ) : (() => {
        const totalRev = rows.reduce((s, r) => s + r.revenue, 0);
        const totalAct = rows.reduce((s, r) => s + r.paid_count + r.pending_count, 0);
        const share = (r) => totalRev > 0 ? r.revenue / totalRev : (totalAct > 0 ? (r.paid_count + r.pending_count) / totalAct : 0);
        return (
        <table className="w-full text-sm" data-testid="sales-table">
          <thead className="text-start opacity-50">
            <tr className="border-b border-black/10 dark:border-white/10">
              <th className="py-1.5 pe-3 font-medium">{TL("Application")}</th>
              <th className="py-1.5 pe-3 font-medium">{TL("Paid")}</th>
              <th className="py-1.5 pe-3 font-medium">{TL("Pending")}</th>
              <th className="py-1.5 font-medium text-end">Revenue{totalRev === 0 ? " · share" : ""}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.product} className="border-b border-black/5 dark:border-white/5" data-testid={`sales-row-${r.product}`}>
                <td className="py-2 pe-3">
                  <div>{r.product}</div>
                  <div className="mt-1 h-1.5 w-full max-w-[240px] overflow-hidden rounded-full bg-black/10 dark:bg-white/10" data-testid={`sales-bar-${r.product}`}
                    title={`${Math.round(share(r) * 100)}% of ${totalRev > 0 ? "revenue" : "activity"}`}>
                    <div className="h-full rounded-full bg-[var(--primary)]" style={{ width: `${Math.max(2, Math.round(share(r) * 100))}%` }} />
                  </div>
                </td>
                <td className="py-2 pe-3">{r.paid_count}</td>
                <td className="py-2 pe-3 opacity-60">{r.pending_count}</td>
                <td className="py-2 text-end font-medium">{r.revenue.toFixed(2)} {cur}
                  <span className="ms-1 text-xs font-normal opacity-50">({Math.round(share(r) * 100)}%)</span></td>
              </tr>
            ))}
          </tbody>
        </table>
        );
      })()}
    </div>
  );
}

function OutreachTile() {
  const [ov, setOv] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { client.get("/marketing/overview").then((r) => setOv(r.data)).catch(() => {}); }, []);
  if (!ov) return null;
  const c = ov.contacts || {};
  const lc = ov.last_campaign;
  return (
    <div data-testid="dashboard-outreach-tile" onClick={() => navigate("/app/enabler")}
      className="block cursor-pointer rounded-xl glass p-6 lift">
      <div className="mb-4 flex items-center gap-2">
        <Megaphone size={20} className="text-[var(--primary)]" />
        <span className="font-head text-base">{TL("Outreach overview")}</span>
        <span className="ms-auto text-xs opacity-50">{TL("Open Master Enabler →")}</span>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <div>
          <div className="mb-1 text-xs uppercase tracking-wide opacity-50">{TL("Contact lists")}</div>
          <div className="flex flex-wrap gap-3 text-sm">
            <span className="flex items-center gap-1 text-[#24A148]"><Phone size={13} /> {c.call || 0} Call</span>
            <span className="flex items-center gap-1 text-[#DA1E28]"><PhoneOff size={13} /> {c.dnc || 0} DNC</span>
            <span className="flex items-center gap-1 opacity-70"><HelpCircle size={13} /> {c.unknown || 0} Unknown</span>
          </div>
          <div className="mt-1 text-xs opacity-50">{c.total || 0} total contacts</div>
        </div>
        <div>
          <div className="mb-1 text-xs uppercase tracking-wide opacity-50">{TL("Scheduled")}</div>
          <div className="flex items-center gap-2 text-sm"><CalendarClock size={15} className="text-[var(--primary)]" /> {ov.scheduled_count || 0} active</div>
          {ov.next_scheduled
            ? <div className="mt-1 text-xs opacity-60">Next: {ov.next_scheduled.schedule_type} · {(ov.next_scheduled.next_run || "").slice(0, 16).replace("T", " ")}</div>
            : <div className="mt-1 text-xs opacity-50">{TL("No upcoming sends")}</div>}
        </div>
        <div>
          <div className="mb-1 text-xs uppercase tracking-wide opacity-50">{TL("Last campaign")}</div>
          {lc
            ? <><div className="text-sm">{lc.recipients} sent · <span className="text-[#24A148]">{lc.delivered} delivered</span>{lc.undelivered
                ? <> · <button data-testid="outreach-undelivered" onClick={(e) => { e.stopPropagation(); navigate(`/app/enabler?campaign=${lc.id}`); }}
                    className="underline decoration-dotted text-[#9e7b00] dark:text-[#F1C21B] hover:opacity-80">{lc.undelivered} undelivered</button></>
                : ""}</div>
                <div className="mt-1 text-xs opacity-50 capitalize">{lc.category} · {(lc.created_at || "").slice(0, 10)}</div></>
            : <div className="text-sm opacity-50">{TL("No campaigns sent yet")}</div>}
        </div>
      </div>
    </div>
  );
}

function SecurityTile() {
  const [ov, setOv] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { client.get("/admin/security-overview").then((r) => setOv(r.data)).catch(() => {}); }, []);
  if (!ov) return null;
  const Stat = ({ label, value, alert }) => (
    <div>
      <div className={`text-2xl font-head ${alert ? "text-[#DA1E28]" : ""}`}>{value}</div>
      <div className="text-xs uppercase tracking-wide opacity-50">{label}</div>
    </div>
  );
  return (
    <div data-testid="dashboard-security-tile" onClick={() => navigate("/app/runners")}
      className="block cursor-pointer rounded-xl glass p-6 lift">
      <div className="mb-4 flex items-center gap-2">
        <ShieldCheck size={20} className="text-[var(--primary)]" />
        <span className="font-head text-base">{TL("Security overview")}</span>
        <span className="ms-auto text-xs opacity-50">{TL("Manage runners →")}</span>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <Stat label={TL("Blocked runners")} value={ov.blocked_runners} alert={ov.blocked_runners > 0} />
        <Stat label={TL("Pending change requests")} value={ov.pending_changes} alert={ov.pending_changes > 0} />
        <Stat label={TL("Password resets (30d)")} value={ov.recent_password_resets} />
      </div>
    </div>
  );
}

const ALERT_DOT = { critical: "#DA1E28", warning: "#F1C21B", info: "var(--primary)" };

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function PortalActivityTile() {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const st = await client.get("/portal/status");
        if (!st.data.enabled) return;
        const v = await client.get("/portal/views");
        if (!v.data.recent?.length) return;
        setInfo({ ...v.data, expired: st.data.expired });
      } catch { /* no portal / not unlocked */ }
    })();
  }, []);
  if (!info) return null;
  const last = info.recent[0];
  return (
    <Link to="/app/holdings" data-testid="dashboard-portal-activity"
      className="flex flex-wrap items-center gap-3 rounded-xl glass px-4 py-3 text-sm lift">
      <Eye size={18} className="text-[var(--primary)]" />
      <span>
        {TL("Your principal / advisor portal was")} <b data-testid="portal-activity-last">{TL("last opened")} {timeAgo(last.at)}</b>
        <span className="opacity-60"> · {last.device} · {info.count} {info.count === 1 ? TL("view") : TL("views")}</span>
      </span>
      {info.expired
        ? <span className="ms-auto rounded-full bg-[#DA1E28]/15 px-2 py-0.5 text-xs font-medium text-[#DA1E28]" data-testid="portal-activity-expired">{TL("link expired")}</span>
        : <span className="ms-auto text-xs opacity-50">{TL("Manage →")}</span>}
    </Link>
  );
}


function EquityIncomeTile() {
  const [ov, setOv] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { client.get("/equity-forecast").then((r) => setOv(r.data)).catch(() => {}); }, []);
  if (!ov || (ov.dividends.count === 0 && ov.lending.count === 0)) return null;
  const money = (v) => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
  const Stat = ({ label, value, green }) => (
    <div>
      <div className={`text-2xl font-head ${green ? "text-green-700 dark:text-green-400" : ""}`}>{value}</div>
      <div className="text-xs uppercase tracking-wide opacity-50">{label}</div>
    </div>
  );
  return (
    <div data-testid="dashboard-equity-income-tile" onClick={() => navigate("/app/holdings")}
      className="block cursor-pointer rounded-xl glass p-6 lift">
      <div className="mb-4 flex items-center gap-2">
        <CalendarClock size={20} className="text-[var(--primary)]" />
        <span className="font-head text-base">Scheduled equity income — {ov.month_label}</span>
        <span className="ms-auto text-xs opacity-50">{TL("Open Holdings →")}</span>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <Stat label={TL("Dividends due")} value={ov.dividends.count} green />
        <Stat label={TL("Lending returns due")} value={ov.lending.count} green />
        <Stat label={TL("Total legs")} value={ov.dividends.count + ov.lending.count} />
      </div>
      {ov.by_currency.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="equity-income-by-currency">
          {ov.by_currency.map((c) => (
            <span key={c.currency} data-testid={`equity-income-cur-${c.currency}`}
              className="rounded-full border border-black/10 px-3 py-1 text-sm dark:border-white/15">
              <b className="text-green-700 dark:text-green-400">+{money(c.amount)}</b> <span className="opacity-60">{c.currency}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}


function CurrencidaAccountsTile() {
  const [rows, setRows] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { client.get("/ledger/currency-overview").then((r) => setRows(r.data.accounts || [])).catch(() => setRows([])); }, []);
  if (!rows || rows.length === 0) return null;
  const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  const utilColor = (u) => (u >= 90 ? "text-[#DA1E28]" : u >= 70 ? "text-[#C99415]" : "opacity-70");
  return (
    <div data-testid="dashboard-currencida-tile" className="rounded-xl glass p-6">
      <div className="mb-4 flex items-center gap-2">
        <Wallet size={20} className="text-[var(--primary)]" />
        <span className="font-head text-base">{TL("Your Currencida accounts")}</span>
        <span className="ms-auto text-xs opacity-50">{rows.length} {rows.length === 1 ? TL("account") : TL("accounts")} · {TL("click to open")}</span>
      </div>
      <div className="divide-y divide-black/5 dark:divide-white/5">
        {rows.map((a) => {
          const isCredit = a.kind && a.kind !== "Deposit";
          const dueSoon = isCredit && a.min_payment > 0 && a.payment_due;
          return (
            <button key={a.id} type="button" onClick={() => navigate(`/ledger/currency/${a.id}`)}
              data-testid={`dash-acct-${a.id}`}
              className="flex w-full flex-wrap items-center gap-x-4 gap-y-1 py-2.5 text-start transition hover:bg-black/[0.03] dark:hover:bg-white/[0.04]">
              <span className="flex items-center gap-2 font-medium">
                {isCredit ? <CreditCard size={15} className="text-[#C99415]" /> : <Wallet size={15} className="opacity-50" />}
                {a.name}
              </span>
              {isCredit && <span className="rounded-full bg-[#C99415]/15 px-2 py-0.5 text-[10px] font-medium text-[#8a6410] dark:text-[#e8c774]">{a.kind}</span>}
              {isCredit ? (
                <span className="ms-auto flex flex-wrap items-center gap-x-4 gap-y-0.5 text-sm">
                  <span><span className="opacity-55">{TL("Owed")}</span> <b className="text-[#DA1E28]">{money(a.owed)}</b></span>
                  <span><span className="opacity-55">{TL("Avail")}</span> <b className={a.available < 0 ? "text-[#DA1E28]" : "text-[#0E9F6E]"}>{money(a.available)}</b></span>
                  <span><span className="opacity-55">{TL("Util")}</span> <b className={utilColor(a.utilisation)}>{Number(a.utilisation).toFixed(0)}%</b></span>
                  {dueSoon && <span className="rounded-full bg-[#DA1E28]/10 px-2 py-0.5 text-[11px] font-medium text-[#DA1E28]" data-testid={`dash-acct-due-${a.id}`}>Min {money(a.min_payment)} by {a.payment_due}</span>}
                </span>
              ) : (
                <span className="ms-auto text-sm"><span className="opacity-55">{TL("Balance")}</span> <b>{money(a.balance)}</b></span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}



function AdminAlertsTile() {
  const [filter, setFilter] = useState("all");
  const [data, setData] = useState(null);
  const load = (f = filter) => client.get("/admin/alerts", { params: { status: f } }).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(filter); /* eslint-disable-next-line */ }, [filter]);
  if (!data) return null;
  const toggleRead = async (a) => {
    await client.post(`/admin/alerts/${a.id}/${a.read ? "unread" : "read"}`);
    load();
  };
  const markAll = async () => { await client.post("/admin/alerts/read-all"); load(); };
  const clearRead = async () => { if (!window.confirm("Delete all read alerts?")) return; await client.post("/admin/alerts/clear-read"); load(); };
  const clearAll = async () => { if (!window.confirm("Delete ALL alerts (read and unread)? This cannot be undone.")) return; await client.post("/admin/alerts/clear-all"); load(); };
  const TABS = [["all", "All"], ["unread", "Unread"], ["read", "Read"]];
  return (
    <div data-testid="dashboard-alerts-tile" className="rounded-xl glass p-6">
      <div className="mb-4 flex items-center gap-2">
        {data.unread > 0 ? <BellRing size={20} className="text-[#DA1E28]" /> : <Bell size={20} className="text-[var(--primary)]" />}
        <span className="font-head text-base">{TL("System alerts")}</span>
        {data.unread > 0 && <span data-testid="alerts-unread-badge" className="rounded-full bg-[#DA1E28] px-2 py-0.5 text-xs font-medium text-white">{data.unread} unread</span>}
        <button onClick={markAll} disabled={data.unread === 0} data-testid="alerts-mark-all-read"
          className="ms-auto text-xs text-[var(--primary)] hover:underline disabled:opacity-40">{TL("Mark all read")}</button>
        <button onClick={clearRead} disabled={(data.total ?? 0) - (data.unread ?? 0) <= 0} data-testid="alerts-clear-read"
          className="text-xs text-[#DA1E28] hover:underline disabled:opacity-40">{TL("Clear read")}</button>
        <button onClick={clearAll} disabled={(data.total ?? 0) <= 0} data-testid="alerts-clear-all"
          className="text-xs text-[#DA1E28] hover:underline disabled:opacity-40">{TL("Clear all")}</button>
      </div>
      <div className="mb-3 flex gap-1" data-testid="alerts-filter">
        {TABS.map(([v, label]) => (
          <button key={v} onClick={() => setFilter(v)} data-testid={`alerts-filter-${v}`}
            className={`rounded-full px-3 py-1 text-xs ${filter === v ? "bg-[var(--primary)] text-white" : "border border-black/15 dark:border-white/15"}`}>{label}</button>
        ))}
      </div>
      <div className="space-y-2" data-testid="alerts-list">
        {data.items.length === 0 && <div className="text-sm opacity-50">{TL("No alerts to show.")}</div>}
        {data.items.map((a) => (
          <div key={a.id} data-testid={`alert-row-${a.id}`}
            className={`flex items-start gap-3 rounded-md border border-black/10 dark:border-white/10 p-3 ${a.read ? "opacity-60" : ""}`}>
            <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full" style={{ background: ALERT_DOT[a.severity] || ALERT_DOT.info }} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{a.title}</span>
                {!a.read && <span className="rounded bg-[var(--primary)]/15 px-1.5 text-[10px] uppercase tracking-wide text-[var(--primary)]">{TL("new")}</span>}
              </div>
              <div className="text-xs opacity-70">{a.message}</div>
              <div className="mt-1 text-[11px] opacity-40">{(a.created_at || "").slice(0, 16).replace("T", " ")} UTC</div>
            </div>
            <button onClick={() => toggleRead(a)} data-testid={`alert-toggle-${a.id}`} title={a.read ? "Mark unread" : "Mark read"}
              className="flex-shrink-0 rounded-md border border-black/15 dark:border-white/15 p-1.5 lift">
              {a.read ? <RotateCcw size={13} /> : <Check size={13} />}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function EmailHealthTile() {
  const [ov, setOv] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { client.get("/admin/email-health").then((r) => setOv(r.data)).catch(() => {}); }, []);
  if (!ov) return null;
  const tt = ov.totals || {};
  const rateAlert = tt.delivery_rate !== null && tt.delivery_rate < 90;
  const CAT_LABELS = { transactional: "Transactional", sample: "Sample previews", test: "Test sends" };
  return (
    <div data-testid="dashboard-email-health-tile" onClick={() => navigate("/app/settings")}
      className="block cursor-pointer rounded-xl glass p-6 lift">
      <div className="mb-4 flex items-center gap-2">
        <Mail size={20} className="text-[var(--primary)]" />
        <span className="font-head text-base">{TL("Email delivery health")}</span>
        <span className="ms-auto text-xs opacity-50">Last {ov.days}d · Settings →</span>
      </div>
      <div className="grid gap-4 sm:grid-cols-4">
        <div>
          <div className="text-2xl font-head">{tt.total ?? 0}</div>
          <div className="text-xs uppercase tracking-wide opacity-50">{TL("Emails sent")}</div>
        </div>
        <div>
          <div className="text-2xl font-head">{tt.sent ?? 0}</div>
          <div className="text-xs uppercase tracking-wide opacity-50">{TL("Delivered")}</div>
        </div>
        <div>
          <div className={`text-2xl font-head ${tt.fallback > 0 ? "text-[#F1C21B]" : ""}`}>{tt.fallback ?? 0}</div>
          <div className="text-xs uppercase tracking-wide opacity-50">{TL("Fallback (logged)")}</div>
        </div>
        <div>
          <div className={`text-2xl font-head ${rateAlert ? "text-[#DA1E28]" : ""}`}>{tt.delivery_rate === null || tt.delivery_rate === undefined ? "—" : `${tt.delivery_rate}%`}</div>
          <div className="text-xs uppercase tracking-wide opacity-50">{TL("Delivery rate")}</div>
        </div>
      </div>
      {(ov.by_category || []).length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="email-health-by-category">
          {ov.by_category.map((c) => (
            <span key={c.category} className="rounded-full border border-black/10 dark:border-white/15 px-3 py-1 text-xs">
              {CAT_LABELS[c.category] || c.category}: <b>{c.sent}</b>/{c.total}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function rangeForPreset(preset) {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth();
  const fmt = (d) => d.toISOString().slice(0, 10);
  if (preset === "this_month") return { frm: fmt(new Date(y, m, 1)), to: fmt(new Date(y, m + 1, 0)) };
  if (preset === "last_month") return { frm: fmt(new Date(y, m - 1, 1)), to: fmt(new Date(y, m, 0)) };
  if (preset === "this_quarter") { const q = Math.floor(m / 3) * 3; return { frm: fmt(new Date(y, q, 1)), to: fmt(new Date(y, q + 3, 0)) }; }
  if (preset === "this_year") return { frm: fmt(new Date(y, 0, 1)), to: fmt(new Date(y, 11, 31)) };
  return { frm: "", to: "" }; // all time
}

const PRESETS = [
  { key: "all", label: "All time" },
  { key: "this_month", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "this_quarter", label: "This quarter" },
  { key: "this_year", label: "This year" },
];

function AuditTile() {
  const [ov, setOv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preset, setPreset] = useState("all");
  const [testApp, setTestApp] = useState("currency");
  const [posting, setPosting] = useState(false);
  const [tick, setTick] = useState(0);
  const [cfg, setCfg] = useState(null);
  const [msg, setMsg] = useState("");

  const params = rangeForPreset(preset);
  useEffect(() => {
    client.get("/admin/audit-report", { params }).then((r) => setOv(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, tick]);
  useEffect(() => { client.get("/settings").then((r) => setCfg(r.data || {})).catch(() => {}); }, []);

  const saveCfg = async (patch) => {
    const next = { ...(cfg || {}), ...patch };
    setCfg(next);
    await client.put("/settings", {
      revenue_report_enabled: !!next.revenue_report_enabled,
      revenue_recipient: next.revenue_recipient || "",
      revenue_day: Number(next.revenue_day) || 1,
    });
  };
  const sendNow = async () => {
    setPosting(true); setMsg("");
    try {
      const r = await client.post("/admin/revenue-report/send-now", {});
      setMsg(`Sent to ${r.data.recipient} (${r.data.delivery?.status})`);
    } catch (e) { setMsg("Could not send — set a recipient first."); }
    finally { setPosting(false); }
  };

  const blobDownload = async (path, filename) => {
    setBusy(true);
    try {
      const res = await client.get(path, { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } finally { setBusy(false); }
  };

  const recordTest = async () => {
    setPosting(true);
    try {
      await client.post("/admin/test-purchase", { app_type: testApp });
      setTick((n) => n + 1);
    } finally { setPosting(false); }
  };

  const clearTest = async () => {
    setPosting(true);
    try {
      await client.delete("/admin/test-purchases");
      setTick((n) => n + 1);
    } finally { setPosting(false); }
  };

  if (!ov) return null;
  const tt = ov.totals || {};
  const cur = tt.currency || "USD";
  const money = (v) => (v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const Stat = ({ label, value }) => (
    <div>
      <div className="text-2xl font-head">{value}</div>
      <div className="text-xs uppercase tracking-wide opacity-50">{label}</div>
    </div>
  );
  const APPS = [
    { v: "family_tree", l: "Family Tree" }, { v: "holdings", l: "Holdings" },
    { v: "currency", l: "Currency" }, { v: "benefits", l: "Benefits" },
  ];
  return (
    <div data-testid="dashboard-audit-tile" className="rounded-xl glass p-6">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <FileBarChart size={20} className="text-[var(--primary)]" />
        <span className="font-head text-base">{TL("License & payment audit")}</span>
        <div className="ms-auto flex flex-wrap items-center gap-2">
          <select data-testid="audit-range" value={preset} onChange={(e) => setPreset(e.target.value)}
            className="rounded-md border border-black/15 dark:border-white/15 bg-transparent px-2 py-1.5 text-sm">
            {PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
          <button onClick={() => blobDownload("/admin/audit-report.csv", `audit_revenue_${preset}.csv`)} disabled={busy}
            data-testid="audit-download-csv"
            className="flex items-center gap-1.5 rounded-md border border-black/15 dark:border-white/15 px-3 py-1.5 text-sm lift disabled:opacity-60">
            <FileSpreadsheet size={14} /> CSV
          </button>
          <button onClick={() => blobDownload("/admin/audit-report.pdf", `audit_report_${preset}.pdf`)} disabled={busy}
            data-testid="audit-download-pdf"
            className="flex items-center gap-1.5 rounded-md bg-[var(--primary)] px-3 py-1.5 text-sm text-white lift disabled:opacity-60">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} PDF report
          </button>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-4">
        <Stat label={TL("Total licenses")} value={tt.licenses ?? 0} />
        <Stat label={TL("Activated")} value={tt.activated ?? 0} />
        <Stat label={TL("Paid unlocks")} value={tt.paid ?? 0} />
        <Stat label={`Revenue (${cur})`} value={money(tt.revenue)} />
      </div>
      {(ov.per_app || []).length > 0 && (
        <div className="mt-5 overflow-hidden rounded-lg border border-black/10 dark:border-white/10">
          <table className="w-full text-sm" data-testid="audit-per-app">
            <thead>
              <tr className="bg-black/[0.03] dark:bg-white/[0.04] text-start text-xs uppercase tracking-wide opacity-60">
                <th className="px-3 py-2">{TL("Application")}</th>
                <th className="px-3 py-2 text-end">{TL("Activations")}</th>
                <th className="px-3 py-2 text-end">{TL("Paid")}</th>
                <th className="px-3 py-2 text-end">Revenue ({cur})</th>
              </tr>
            </thead>
            <tbody>
              {ov.per_app.map((a) => (
                <tr key={a.app_type} className="border-t border-black/5 dark:border-white/5">
                  <td className="px-3 py-2">{a.app_label}</td>
                  <td className="px-3 py-2 text-end">{a.activations}</td>
                  <td className="px-3 py-2 text-end">{a.paid}</td>
                  <td className="px-3 py-2 text-end">{money(a.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-black/10 dark:border-white/10 pt-4">
        <FlaskConical size={15} className="text-[var(--primary)]" />
        <span className="text-xs opacity-60">{TL("Stripe test mode — record a simulated paid purchase to preview revenue:")}</span>
        <select data-testid="audit-test-app" value={testApp} onChange={(e) => setTestApp(e.target.value)}
          className="rounded-md border border-black/15 dark:border-white/15 bg-transparent px-2 py-1.5 text-sm">
          {APPS.map((a) => <option key={a.v} value={a.v}>{a.l}</option>)}
        </select>
        <button onClick={recordTest} disabled={posting} data-testid="audit-test-purchase"
          className="flex items-center gap-1.5 rounded-md border border-[var(--primary)] px-3 py-1.5 text-sm text-[var(--primary)] lift disabled:opacity-60">
          {posting ? <Loader2 size={14} className="animate-spin" /> : <FlaskConical size={14} />} Record test purchase
        </button>
        {(tt.paid ?? 0) > 0 && (
          <button onClick={clearTest} disabled={posting} data-testid="audit-clear-test"
            className="flex items-center gap-1.5 rounded-md border border-[#DA1E28]/40 px-3 py-1.5 text-sm text-[#DA1E28] lift disabled:opacity-60">
            <Trash2 size={14} /> Clear test data
          </button>
        )}
      </div>
      <div className="mt-4 border-t border-black/10 dark:border-white/10 pt-4" data-testid="revenue-email-config">
        <div className="flex flex-wrap items-center gap-3">
          <Mail size={15} className="text-[var(--primary)]" />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid="revenue-email-enabled"
              checked={!!cfg?.revenue_report_enabled}
              onChange={(e) => saveCfg({ revenue_report_enabled: e.target.checked })} />
            Email me a monthly revenue report
          </label>
          <input type="email" placeholder={TL("recipient@email.com")} data-testid="revenue-email-recipient"
            value={cfg?.revenue_recipient || ""}
            onChange={(e) => setCfg({ ...(cfg || {}), revenue_recipient: e.target.value })}
            onBlur={(e) => saveCfg({ revenue_recipient: e.target.value })}
            className="rounded-md border border-black/15 dark:border-white/15 bg-transparent px-2 py-1.5 text-sm" />
          <label className="flex items-center gap-1.5 text-sm opacity-70">on day
            <input type="number" min="1" max="28" data-testid="revenue-email-day"
              value={cfg?.revenue_day || 1}
              onChange={(e) => setCfg({ ...(cfg || {}), revenue_day: e.target.value })}
              onBlur={(e) => saveCfg({ revenue_day: e.target.value })}
              className="w-16 rounded-md border border-black/15 dark:border-white/15 bg-transparent px-2 py-1.5 text-sm" />
          </label>
          <button onClick={sendNow} disabled={posting} data-testid="revenue-send-now"
            className="flex items-center gap-1.5 rounded-md border border-black/15 dark:border-white/15 px-3 py-1.5 text-sm lift disabled:opacity-60">
            {posting ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Send now
          </button>
          {msg && <span className="text-xs opacity-60" data-testid="revenue-send-msg">{msg}</span>}
        </div>
      </div>
    </div>
  );
}

function CoverageTile() {
  const [cov, setCov] = useState(null);
  useEffect(() => { client.get("/admin/support/coverage").then((r) => setCov(r.data)).catch(() => {}); }, []);
  if (!cov || cov.totals.seats === 0) return null;
  const t = cov.totals;
  const langName = (c) => LANGS.find((l) => l.code === c)?.native || c;
  const rows = cov.per_language.filter((r) => r.seats > 0 || r.actions > 0);
  const maxA = Math.max(1, ...cov.per_language.map((r) => r.actions));
  const Stat = ({ label, value }) => (
    <div><div className="text-2xl font-head">{value}</div><div className="text-xs uppercase tracking-wide opacity-50">{label}</div></div>
  );
  return (
    <div data-testid="dashboard-coverage-tile" className="rounded-xl glass p-6">
      <div className="mb-4 flex items-center gap-2">
        <Headset size={20} className="text-[var(--primary)]" />
        <span className="font-head text-base">{TL("Support coverage")}</span>
        <Link to="/app/support" className="ms-auto text-sm text-[var(--primary)] hover:underline">{TL("Open console →")}</Link>
      </div>
      <div className="grid gap-4 sm:grid-cols-4">
        <Stat label={TL("Support seats")} value={t.seats} />
        <Stat label={TL("Online now")} value={`${t.active}/${cov.caps.total}`} />
        <Stat label={TL("Actions logged")} value={t.actions} />
        <Stat label={TL("Languages covered")} value={`${t.languages_covered}/${t.languages_total}`} />
      </div>
      {cov.uncovered.length > 0 && (
        <div className="mt-3 flex items-center gap-2 rounded-md border border-[#F1C21B]/50 bg-[#F1C21B]/10 px-3 py-2 text-xs" data-testid="coverage-gap">
          <AlertTriangle size={14} className="text-[#8a6d00] dark:text-[#F1C21B]" />
          <span>No support seats for: {cov.uncovered.join(", ")}</span>
        </div>
      )}
      {rows.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="coverage-by-language">
          {rows.map((r) => (
            <div key={r.code} className="text-xs">
              <div className="mb-0.5 flex justify-between">
                <span>{langName(r.code)} ({r.code})</span>
                <span className="opacity-60">{r.seats} seat(s) · {r.active} online · {r.actions} action(s)</span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-black/10 dark:bg-white/10">
                <div className="h-full bg-[var(--primary)]" style={{ width: `${(r.actions / maxA) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user, lang, isAdmin } = useApp();
  const { breaches } = useBreaches();
  const [sum, setSum] = useState({});
  const [blocked, setBlocked] = useState([]);
  const [unreadAlerts, setUnreadAlerts] = useState(0);
  const [queuedEmails, setQueuedEmails] = useState(0);
  useEffect(() => { client.get("/summary").then((r) => setSum(r.data)); }, []);
  useEffect(() => { if (isAdmin) client.get("/admin/blocked-runners").then((r) => setBlocked(r.data)).catch(() => {}); }, [isAdmin]);
  useEffect(() => { if (isAdmin) client.get("/admin/alerts/unread-count").then((r) => setUnreadAlerts(r.data.unread)).catch(() => {}); }, [isAdmin]);
  useEffect(() => { if (isAdmin) client.get("/settings/email-quota").then((r) => setQueuedEmails(r.data.queued)).catch(() => {}); }, [isAdmin]);

  return (
    <div className="space-y-8 fadeup">
      <div>
        <h1 className="font-head text-4xl font-light">{BRAND}</h1>
        <p className="opacity-60">{TL("Welcome back,")} {user?.name}. {TL("Five interwoven applications, one secure suite.")}</p>
      </div>
      <MaintenanceBanner />
      {isAdmin && blocked.length > 0 && (
        <Link to="/app/runners" data-testid="dashboard-blocked-banner"
          className="flex items-center gap-3 rounded-xl border border-[#DA1E28]/50 bg-[#DA1E28]/10 px-4 py-3 text-sm lift">
          <ShieldAlert size={18} className="text-[#DA1E28]" />
          <span><b>{blocked.length}</b> runner{blocked.length === 1 ? "" : "s"} auto-blocked after declining a change. Click to review and restore access.</span>
        </Link>
      )}
      {isAdmin && queuedEmails > 0 && (
        <Link to="/app/settings" data-testid="dashboard-email-queue-banner"
          className="flex items-center gap-3 rounded-xl border border-[#F1C21B]/50 bg-[#F1C21B]/10 px-4 py-3 text-sm lift">
          <Mail size={18} className="text-[#9e7b00] dark:text-[#F1C21B]" />
          <span><b data-testid="dashboard-email-queue-count">{queuedEmails}</b> marketing email{queuedEmails === 1 ? "" : "s"} queued by the daily quota guard — will send automatically once quota frees up. Manage →</span>
        </Link>
      )}
      {breaches.length > 0 && (
        <Link to="/app/alerts" data-testid="dashboard-breach-banner"
          className="flex items-center gap-3 rounded-xl border border-[#DA1E28]/50 bg-[#DA1E28]/10 px-4 py-3 text-sm lift">
          <AlertTriangle size={18} className="text-[#DA1E28]" />
          <span><b>{breaches.length}</b> alert{breaches.length > 1 ? "s" : ""} breached a preset limit. Review them in Alerts →</span>
        </Link>
      )}
      {isAdmin && unreadAlerts > 0 && (
        <a href="#system-alerts" data-testid="dashboard-alerts-banner"
          className="flex items-center gap-3 rounded-xl border border-[#DA1E28]/50 bg-[#DA1E28]/10 px-4 py-3 text-sm lift">
          <BellRing size={18} className="text-[#DA1E28]" />
          <span><b>{unreadAlerts}</b> unread system alert{unreadAlerts === 1 ? "" : "s"}. Review the System alerts log below.</span>
        </a>
      )}
      {isAdmin && <FilingWallTile />}
      {isAdmin && <OutreachTile />}
      <PortalActivityTile />
      {!isAdmin && <MyServiceRenewalsTile />}
      {isAdmin && <SalesTile />}
      {isAdmin && <NewsletterAnalyticsTile />}
      {isAdmin && <ServiceRenewalsTile />}
      {isAdmin && <OverdueAccountsTile />}
      {isAdmin && <SecurityTile />}
      {isAdmin && <EmailHealthTile />}
      {isAdmin && <div id="system-alerts"><AdminAlertsTile /></div>}
      {isAdmin && <CoverageTile />}
      {isAdmin && <AuditTile />}
      <EquityIncomeTile />
      <CurrencidaAccountsTile />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {CARDS.map((c, i) => (
          <Card key={c.key} className="space-y-4" style={{ animationDelay: `${i * 60}ms` }}>
            <div className="flex items-start justify-between">
              {c.icon
                ? <c.icon size={28} className="text-[var(--primary)]" />
                : c.img
                  ? <img src={c.img} alt={APP_FULL[c.manual]} data-testid={`app-logo-${c.label}`} className="h-14 w-14 object-contain" />
                  : <span className="text-xs uppercase tracking-wide opacity-50">{TL("Benefits")}</span>}
              <span className="font-head text-3xl">{sum[c.key] ?? 0}</span>
            </div>
            {c.wordmark && <img src={c.wordmark} alt={APP_FULL[c.manual]} data-testid={`app-wordmark-${c.label}`} className="h-10 w-auto max-w-full object-contain object-left" />}
            {c.banner && <img src={c.banner} alt={APP_FULL[c.manual]} data-testid={`app-banner-${c.label}`} className="mx-auto h-44 w-auto object-contain" />}
            <div className="font-head text-base leading-snug">{appTitle(c.manual)}</div>
            <div className="flex flex-wrap gap-2">
              <Link to={c.to} data-testid={`go-${c.label}`}
                className="rounded-md bg-[var(--primary)] px-3 py-1.5 text-sm text-white lift">{TL("Open")}</Link>
              <button onClick={() => downloadManual(c.manual)} data-testid={`manual-${c.manual}`}
                className="flex items-center gap-1 rounded-md border border-black/15 dark:border-white/15 px-3 py-1.5 text-sm lift">
                <Download size={14} /> {t("download_manual", lang)}
              </button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
